import React, { useState, useEffect } from 'react';
import { SatsangAudio } from '../../types';
import { Play, Pause, X, ChevronUp, Music2, Loader2 } from 'lucide-react';
import { nativePauseAudio, nativeResumeAudio } from '../../services/nativeAudio';

interface Props {
  currentAudio: SatsangAudio;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  onExpand: () => void;
  onClose: () => void;
  progress: number;
  audioRef: React.RefObject<HTMLAudioElement>;
  isNative?: boolean;
}

export const MiniPlayer: React.FC<Props> = ({ 
  currentAudio, isPlaying, setIsPlaying, onExpand, onClose, progress, audioRef, isNative 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  useEffect(() => {
    if (isNative) return; // No HTML5 audio errors on native
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => setHasError(true);
    audio.addEventListener('error', handleError);
    return () => audio.removeEventListener('error', handleError);
  }, [audioRef, isNative]);

  // Clear buffering when progress starts moving
  useEffect(() => {
    if (progress > 0 && isPlaying) {
      setIsBuffering(false);
    }
  }, [progress, isPlaying]);

  // New track = buffering
  useEffect(() => {
    setIsBuffering(true);
  }, [currentAudio?.id]);

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (isNative) {
      if (isPlaying) {
        nativePauseAudio();
      } else {
        nativeResumeAudio();
        setIsBuffering(true);
      }
      setIsPlaying(!isPlaying);
      return;
    }
    if (hasError || !audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsBuffering(true);
      audioRef.current.play().catch(e => {
        console.error('Play failed:', e);
        setHasError(true);
        setIsBuffering(false);
      });
      setIsPlaying(true);
    }
  };

  return (
    <div 
      className="flex-shrink-0 bg-cream dark:bg-stone-900 border-t-2 border-saffron/40 dark:border-saffron/20"
      data-testid="mini-player"
    >
      {/* Progress Bar - Thin line at top */}
      <div className="h-0.5 bg-saffron/10 dark:bg-stone-700">
        <div 
          className="h-full bg-saffron transition-all duration-200"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Sound Animation / Music Icon */}
        <div 
          onClick={onExpand}
          className="w-10 h-10 rounded-lg bg-gradient-to-br from-saffron/20 to-orange-100 dark:from-saffron/30 dark:to-stone-700 flex items-center justify-center flex-shrink-0 cursor-pointer"
        >
          {isPlaying ? (
            <div className="flex gap-[2px] items-center h-4">
              <div className="w-[2px] bg-saffron rounded-full animate-[soundbar_0.5s_ease-in-out_infinite] h-1.5"></div>
              <div className="w-[2px] bg-saffron rounded-full animate-[soundbar_0.5s_ease-in-out_infinite_0.1s] h-4"></div>
              <div className="w-[2px] bg-saffron rounded-full animate-[soundbar_0.5s_ease-in-out_infinite_0.2s] h-2.5"></div>
            </div>
          ) : (
            <Music2 size={18} className="text-saffron" />
          )}
        </div>
        
        {/* Track Info */}
        <div 
          onClick={onExpand}
          className="flex-1 min-w-0 cursor-pointer"
        >
          <p className="text-sm font-semibold truncate text-stone-700 dark:text-stone-100">
            {currentAudio.title}
          </p>
          <p className="text-[11px] text-stone-400 dark:text-stone-500 truncate">
            {currentAudio.speaker}
          </p>
        </div>
        
        {/* Controls - Compact */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {/* Play/Pause */}
          <button 
            onClick={handlePlayPause}
            disabled={hasError}
            className="w-9 h-9 rounded-full bg-saffron flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
            data-testid="mini-player-playpause"
          >
            {isBuffering && isPlaying ? (
              <Loader2 size={16} className="text-white animate-spin" />
            ) : isPlaying ? (
              <Pause size={16} className="text-white" />
            ) : (
              <Play size={16} className="text-white ml-0.5" />
            )}
          </button>
          
          {/* Expand Button */}
          <button 
            onClick={onExpand}
            className="p-2 text-stone-400 hover:text-saffron transition-colors"
            data-testid="mini-player-expand"
          >
            <ChevronUp size={18} />
          </button>
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-red-500 transition-colors"
            data-testid="mini-player-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes soundbar {
          0%, 100% { height: 6px; }
          50% { height: 16px; }
        }
      `}</style>
    </div>
  );
};
